/**
 * iter 58 — Forgot Password page (custom flow, Supabase bypassed).
 *
 * Public route at /forgot-password. Submits an email to the backend; backend
 * silently issues a one-time reset token, emails it through LogBook's own
 * SendGrid/Gmail pipeline. We always show the success state regardless of
 * whether the email exists (no enumeration).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { api, formatErr } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setDone(true);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0A0A0A] text-white">
        <img src="/logbook-logo.png" alt="LogBook Legal" className="h-12 w-auto" />
        <div className="space-y-6 max-w-md">
          <div className="label-xs text-white/60">PROCESS SERVING · INTAKE · COMPLIANCE</div>
          <h1 className="heading text-5xl leading-[0.95]">Reset your<br/>password.</h1>
          <p className="text-white/70 leading-relaxed">Enter the email you use to sign in. We&apos;ll email you a one-time link to set a new password.</p>
        </div>
        <div className="text-xs text-white/40 mono">© 2026 LOGBOOK LEGAL</div>
      </div>
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md space-y-6">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="forgot-back-link">
            <ArrowLeft size={12} /> Back to sign in
          </Link>
          <div>
            <div className="label-xs">RECOVERY</div>
            <h2 className="heading text-3xl mt-1">Forgot password?</h2>
          </div>

          {done ? (
            <div className="space-y-3" data-testid="forgot-done">
              <div className="badge badge-default w-full justify-center bg-emerald-50 text-emerald-800 border-emerald-300">
                Check your inbox
              </div>
              <p className="text-sm text-muted-foreground">
                If an account exists for <strong>{email}</strong>, we&apos;ve sent a reset link.
                The link is valid for 30 minutes and can only be used once.
              </p>
              <p className="text-xs text-muted-foreground">
                Didn&apos;t get it? Check spam, then{" "}
                <button
                  type="button"
                  className="underline hover:text-foreground"
                  onClick={() => { setDone(false); setErr(""); }}
                  data-testid="forgot-resend"
                >
                  try again
                </button>.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              <div>
                <label className="label-xs block mb-2">EMAIL</label>
                <input
                  type="email"
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  data-testid="forgot-email-input"
                />
              </div>
              {err && <div className="badge badge-red w-full justify-center" data-testid="forgot-error">{err}</div>}
              <button
                type="submit"
                className="btn btn-primary w-full justify-center"
                disabled={busy || !email.includes("@")}
                data-testid="forgot-submit"
              >
                {busy ? "SENDING…" : "SEND RESET LINK →"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
